import React, { useState, useEffect } from 'react';
import { Link, useLocation } from '@/lib/router';
import { 
  Menu, X, ChevronDown, Phone, Mail,
  Search, ArrowRight, ChevronRight, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuoteModal } from '@/contexts/QuoteModalContext';
import { mainNavigation, footerNavigation } from '@/data/navigation';
import { siteConfig } from '@/data/site';
import SearchModal from '@/components/SearchModal';

interface MainLayoutProps {
  children: React.ReactNode;
}



const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // 手机端菜单导航状态
  const [currentView, setCurrentView] = useState<'main' | string>('main');
  const [activeCategory, setActiveCategory] = useState<typeof mainNavigation[0] | null>(null);
  
  const location = useLocation();
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    setCurrentView('main');
    setActiveCategory(null);
  }, [location.pathname]);

  // 防止滚动穿透 - 当手机菜单打开时锁定body滚动
  useEffect(() => {
    if (!isMobileMenuOpen) {
      // 重置视图状态
      setCurrentView('main');
      setActiveCategory(null);
      return;
    }
    
    // 存储当前滚动位置
    const scrollY = window.scrollY;
    
    // 存储原始样式
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyTouchAction = document.body.style.touchAction;
    const originalBodyPaddingRight = document.body.style.paddingRight;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlTouchAction = document.documentElement.style.touchAction;
    
    // 计算滚动条宽度以防止布局偏移
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // 应用滚动锁定 - 使用固定定位方法以获得更好的兼容性
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.style.width = '100%';
    
    return () => {
      // 恢复原始样式
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.touchAction = originalHtmlTouchAction;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.touchAction = originalBodyTouchAction;
      document.body.style.paddingRight = originalBodyPaddingRight; 
      document.body.style.width = '';
      
      // 恢复滚动位置
      window.scrollTo(0, scrollY);
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // 处理一级菜单点击
  const handleMainMenuClick = (item: typeof mainNavigation[0]) => {
    if (item.children && item.children.length > 0) {
      // 有子项，进入二级视图
      setActiveCategory(item);
      setCurrentView(item.label);
    } else {
      // 无子项，直接跳转
      setIsMobileMenuOpen(false);
    }
  };

  // 返回主菜单
  const handleBackToMain = () => {
    setCurrentView('main');
    setActiveCategory(null);
  };

  // 获取当前导航项的子项
  const getCurrentChildren = () => {
    if (!activeCategory) return [];
    return activeCategory.children || [];
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Top Bar */}
      <div className="bg-[#003263] text-white text-sm">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href={`tel:${siteConfig.phone}`} className="flex items-center gap-1 hover:text-amber-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{siteConfig.phone}</span>
            </a>
            <a href={`mailto:${siteConfig.email}`} className="flex items-center gap-1 hover:text-amber-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">{siteConfig.email}</span>
            </a>
          </div>
          <a 
            href="https://www.keygotech.com/" 
            className="flex items-center gap-1 hover:text-amber-400 transition-colors"
          >
            <span>中文</span>
            <span className="text-xs">CN</span>
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <header 
        className={`sticky top-0 z-40 bg-white transition-shadow duration-300 ${
          isScrolled ? 'shadow-lg' : 'shadow-sm'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/Img/en_logo_1.png" alt="KeyGoTech" className="h-10" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {mainNavigation.map((item) => (
                <div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                      location.pathname.startsWith(item.href) && item.href !== '/'
                        ? 'text-blue-700 bg-blue-50'
                        : location.pathname === item.href
                        ? 'text-blue-700 bg-blue-50'
                        : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                    {item.children && (
                      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                    )}
                  </Link>

                  {/* Mega Menu Dropdown */}
                  {item.children && activeDropdown === item.label && (
                    <div className="absolute top-full left-0 pt-2">
                      <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-6 min-w-[600px]">
                        <div className="grid grid-cols-2 gap-6">
                          {item.children.map((child) => (
                            <div key={child.href}>
                              <Link
                                to={child.href}
                                className="block font-semibold text-slate-900 hover:text-blue-700 mb-2"
                              >
                                {child.label}
                              </Link>
                              {child.children && (
                                <ul className="space-y-1">
                                  {child.children.map((grandChild) => (
                                    <li key={grandChild.href}>
                                      <Link
                                        to={grandChild.href}
                                        className="text-sm text-slate-600 hover:text-blue-700 transition-colors"
                                      >
                                        {grandChild.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="hidden sm:flex text-slate-600 hover:text-blue-700"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search className="w-5 h-5" />
              </Button>
              <Button
                className="hidden sm:flex bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => openModal()}
              >
                Request Quote
              </Button>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={toggleMobileMenu}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Left Slide Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="fixed top-0 left-0 w-[280px] h-full bg-white z-50 lg:hidden shadow-xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
              {currentView !== 'main' ? (
                <button 
                  onClick={handleBackToMain}
                  className="flex items-center gap-2 text-slate-700 hover:text-blue-700"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">Back</span>
                </button>
              ) : (
                <span className="text-sm font-semibold text-slate-900">Menu</span>
              )}
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Content - With Scroll */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Main Menu View */}
              {currentView === 'main' && (
                <div className="flex flex-col">
                  {mainNavigation.map((item) => (
                    <div key={item.href}>
                      {item.children && item.children.length > 0 ? (
                        // 有子项，使用按钮进入二级视图
                        <button
                          onClick={() => handleMainMenuClick(item)}
                          className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="text-slate-800 font-medium text-base">
                            {item.label}
                          </span>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </button>
                      ) : (
                        // 无子项，直接使用 Link 跳转
                        <Link
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="text-slate-800 font-medium text-base">
                            {item.label}
                          </span>
                        </Link>
                      )}
                    </div>
                  ))}
                  {/* Search Button for Mobile */}
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsSearchOpen(true);
                    }}
                    className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-slate-800 font-medium text-base">
                      Search
                    </span>
                    <Search className="w-5 h-5 text-slate-400" />
                  </button>
                  <div className="p-5">
                    <Button 
                      className="bg-amber-500 hover:bg-amber-600 text-white w-full"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        openModal();
                      }}
                    >
                      Request Quote
                    </Button>
                  </div>
                </div>
              )}

              {/* Submenu View */}
              {currentView !== 'main' && activeCategory && (
                <div className="flex flex-col">
                  {/* Category Title */}
                  <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {activeCategory.label}
                    </span>
                  </div>
                  
                  {/* Parent Link */}
                  <Link
                    to={activeCategory.href}
                    className="block px-5 py-3 border-b border-slate-100 text-blue-700 hover:bg-slate-50 font-medium text-sm"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    All {activeCategory.label}
                  </Link>
                  
                  {/* Children */}
                  {getCurrentChildren().map((child) => (
                    <div key={child.href}>
                      <Link
                        to={child.href}
                        className="block px-5 py-3 border-b border-slate-100 text-slate-700 hover:bg-slate-50 hover:text-blue-700 font-medium text-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {child.label}
                      </Link>
                      {child.children && child.children.length > 0 && (
                        <div className="bg-slate-50">
                          {child.children.map((grandChild) => (
                            <Link
                              key={grandChild.href}
                              to={grandChild.href}
                              className="block px-8 py-2.5 border-b border-slate-100 text-slate-500 hover:text-blue-700 text-xs"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {grandChild.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#003263] text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <div className="mb-4">
                <img 
                  src="/Img/en_logo_white.png" 
                  alt="KeyGoTech" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).parentElement!.innerHTML = `
                      <div class="flex items-center gap-2">
                        <div class="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                          <span class="text-blue-900 font-bold text-lg">K</span>
                        </div>
                        <div>
                          <span class="text-xl font-bold">KeyGo</span>
                          <span class="text-sm text-blue-300 block -mt-1">Tech</span>
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
              <p className="text-blue-200 text-sm mb-4">
                Advanced acoustic imaging solutions for industrial inspection, NVH testing, and traffic monitoring.
              </p>
              <div className="space-y-2 text-sm">
                <p>{siteConfig.address.street}</p>
                <p>{siteConfig.address.city}, {siteConfig.address.country} {siteConfig.address.zip}</p>
                <p>{siteConfig.phone}</p>
                <p>{siteConfig.email}</p>
              </div>
            </div>

            {/* Products */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Products</h3>
              <ul className="space-y-2">
                {footerNavigation.products.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} className="text-blue-200 hover:text-white transition-colors text-sm">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Applications */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Applications</h3>
              <ul className="space-y-2">
                {footerNavigation.applications.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} className="text-blue-200 hover:text-white transition-colors text-sm">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Support</h3>
              <ul className="space-y-2">
                {footerNavigation.support.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} className="text-blue-200 hover:text-white transition-colors text-sm">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-white w-full"
                  onClick={() => openModal()}
                >
                  Request Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-blue-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-blue-300 text-sm">
              © {new Date().getFullYear()} {siteConfig.name}. All rights reserved.
            </p>
           {/*   <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy" className="text-blue-300 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-blue-300 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-blue-300 hover:text-white transition-colors">
                Sitemap
              </Link>
            </div>
            */}
          </div>
        </div>
      </footer>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </div>
  );
};

export default MainLayout;
